import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import type { BookingDTO, CustomerDTO } from '@six-pockets/shared';
import { Button } from '@/components/ui';
import { formatClock } from '@/utils/format';
import { NameInput } from './NameInput';
import styles from '@/styles/components/BookingNamesEditor/style.scss';

interface BookingNamesEditorProps {
  booking: BookingDTO;
  /** The name cell it opens under. */
  anchorRef: RefObject<HTMLElement | null>;
  /** Focus the second name when it opens (＋ on a row with one name). */
  focusSecond: boolean;
  onRename: (name: string) => void;
  onPick: (customer: CustomerDTO) => void;
  onRenameSecond: (name: string) => void;
  onPickSecond: (customer: CustomerDTO) => void;
  onClose: () => void;
}

const EDGE = 8;
const WIDTH = 300;

/**
 * Both names of a booking (doubles: one per team), full, each typed or picked from the existing customers. Opened
 * from the row's "Kiran & Vikram" (or its ＋). Each name is saved as on the row: on leaving the field, or at once
 * when a customer is picked; an emptied second name is removed. Drawn on the page body under the name cell, since
 * the card's booking list would cut it off; a press outside, Escape, a scroll or a resize closes it (saving the field
 * being typed in first).
 */
export function BookingNamesEditor({ booking, anchorRef, focusSecond, onRename, onPick, onRenameSecond, onPickSecond, onClose }: BookingNamesEditorProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<CSSProperties | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const width = Math.min(WIDTH, window.innerWidth - 2 * EDGE);
    const left = Math.max(EDGE, Math.min(r.left, window.innerWidth - width - EDGE));
    const height = boxRef.current?.offsetHeight ?? 200;
    const above = window.innerHeight - r.bottom < height + EDGE && r.top > height + EDGE;
    setPlace(above ? { left, width, bottom: window.innerHeight - r.top + 4 } : { left, width, top: r.bottom + 4 });
  }, [anchorRef]);

  useEffect(() => {
    // The name being typed is saved on blur, so blur it before closing (closing alone would drop it).
    const close = () => {
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && boxRef.current?.contains(focused)) focused.blur();
      onCloseRef.current();
    };
    // The customer suggestions are drawn on the body too: pressing or scrolling them is not "outside".
    const inside = (target: EventTarget | null) =>
      target instanceof Node && (!!boxRef.current?.contains(target) || !!(target instanceof Element && target.closest('[role="listbox"]')));
    const onPointer = (e: PointerEvent) => {
      if (!inside(e.target) && !anchorRef.current?.contains(e.target as Node)) close();
    };
    const onScroll = (e: Event) => {
      if (!inside(e.target)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) close();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [anchorRef]);

  const when = formatClock(booking.startedAt);

  return createPortal(
    <div ref={boxRef} role="dialog" aria-label={`Names for the ${when} booking`} className={styles.box} style={place ?? { visibility: 'hidden' }}>
      <span className={styles.title}>Names · {when}</span>
      <p className={styles.hint}>Two names: billing opens one bill for each.</p>
      <NameInput
        value={booking.customerName}
        linked={booking.customerId !== null}
        label={`First name for the ${when} booking`}
        onCommit={onRename}
        onPick={onPick}
        autoFocus={!focusSecond}
        className={styles.field}
      />
      <NameInput
        value={booking.customerName2}
        linked={booking.customerId2 !== null}
        label={`Second name for the ${when} booking`}
        placeholder="Second name"
        onCommit={onRenameSecond}
        onPick={onPickSecond}
        autoFocus={focusSecond}
        className={styles.field}
      />
      <div className={styles.foot}>
        <Button size="sm" variant="primary" onClick={() => onCloseRef.current()} className={styles.done}>
          Done
        </Button>
      </div>
    </div>,
    document.body,
  );
}
